import React, { useEffect, useState, useRef } from 'react';
import { Box, ButtonBase, makeStyles, Typography, InputBase, Checkbox, FormControlLabel } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { AddIcon, DeleteIcon } from '../../../../components/Icon';
import { i18n } from '../../../../language';
import Dialog from '../../../../components/Dialog';
import { dutyAssignAPI } from '../../../../apis';
import { AutoCompleteSelect } from '../../../../components';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiPaper-root': {
      padding: '0',
      margin: '0',
      boxShadow: 'unset',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1.875rem 1.875rem 1.563rem 1.587rem',
  },
  headerTitle: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#2e4469',
  },
  closeIcon: {
    fontSize: '1.375rem',
    color: '#538fff',
    margin: '-12px -12px 0 0',
  },
  label: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#7d86a9',
  },
  infoIcon: {
    color: '#0e70a5',
    width: '1.25rem',
    height: '1.25rem',
    marginBottom: '0.5rem',
    marginLeft: '0.3rem',
  },
  main: {
    padding: '0 1.563rem',
    overflow: 'auto',
  },

  InputBase: ({ error }) => ({
    border: error ? 'solid 1px #f44336' : 'solid 1px #e4e7eb',
    borderRadius: '10px',
    height: '3.5rem',
    width: '100%',
    padding: '0 1.25rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    '&.MuiInputBase-root.Mui-disabled': {
      color: 'blue',
    },
  }),
  select: {
    backgroundColor: '#ffffff',
  },
  cancelBtn: {
    width: '12.5rem',
    height: '3rem',
    borderRadius: '0.625rem',
    border: 'solid 1.5px #538fff',
    color: '#538fff',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
    background: '#f5faff',
  },
  alert: {
    '& .MuiAlert-standardSuccess': {
      padding: '0.2rem 1rem',
    },
  },
  addIcon: {
    fontSize: '1rem',
    color: theme.palette.primary.main,
    marginRight: '0.6rem',
  },
  addBtn: {
    color: '#538fff',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
  },
  deleteIcon: {
    fontSize: '1.25rem',
    color: theme.palette.primary.main,
    marginLeft: '1rem',
  },
  deleteIconDisable: {
    fontSize: '1.25rem',
    color: '#d0d0d0',
    marginLeft: '1rem',
  },
  checkBox: {
    marginBottom: '0.3rem',
    marginLeft: '0rem',
    '& .MuiButtonBase-root': {
      padding: '0',
      paddingRight: '0.938rem',
    },
    '& .MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '& .MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '18',
      fontWeight: '500',
      color: '#2e4469',
    },
    '& .Mui-checked': {
      color: '#28d2ab',
    },
  },
  errorMsg: {
    margin: '3px 0.825rem 0 0.825rem',
    color: '#f44336',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
}));

export default function CreateChatroom({ closePop, open, title, setAlertInfo }) {
  const categoryLevel = {
    CATEGORY_LEVEL: 1,
    SUB_CATEGORY_LEVEL: 2,
  };
  const loginInfo = useSelector((state) => state.loginInfo);
  const classes = useStyles();
  const [confirmDisabled, setConfirmDisabled] = useState(false);
  const [showAll, setShowAll] = useState(true);
  const [inputValues, setInputValues] = useState({
    chats: [],
  });
  const [isObsoleteList, setIsObsoleteList] = useState([]);
  const [sensitiveList, setSensitiveList] = useState([]);
  const [isAddChatroom, setISAddChatroom] = useState(null);
  const chatroomListRef = useRef(null);
  const [categoryList, setCategoryList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);
  const serverErrors = useRef(null);
  const getDropDownLists = async () => {
    try {
      const { result } = await dutyAssignAPI.getDropDownLists({
        wardId: loginInfo.wardId,
        hospId: loginInfo.hospId,
      });
      const obsoleteList = result?.obsolete?.map((item) => {
        let label = '';
        if (item.label === 'Y') label = i18n.t('DUTYASSIGN_CREATE_CHATROOM_ABSOLETE_LABEL_YES');
        if (item.label === 'N') label = i18n.t('DUTYASSIGN_CREATE_CHATROOM_ABSOLETE_LABEL_NO');
        const obsolete = {
          value: item.value,
          text: label,
        };
        return obsolete;
      });
      setIsObsoleteList(obsoleteList);
      result?.sensitive && setSensitiveList(result.sensitive);
      if (result?.category) {
        const emptyData = { value: -999, label: i18n.t('DUTYASSIGN_ROSTER_SELECT_CATEGORY') };
        let list = result.category?.filter((category) => category.extra.level === categoryLevel.CATEGORY_LEVEL);
        list.unshift(emptyData);
        const subList = result.category?.filter(
          (category) => category.extra.level === categoryLevel.SUB_CATEGORY_LEVEL
        );
        setCategoryList(
          list.map((item) => {
            return {
              ...item,
              text: item.label,
            };
          })
        );
        setSubCategoryList(
          subList.map((item) => {
            return {
              ...item,
              text: item.label,
            };
          })
        );
      }
    } catch (error) {
      console.log('-----Get chatroom list error----->', error);
    }
  };

  const getChatroomList = async () => {
    try {
      let { result } = await dutyAssignAPI.getDaDutyChatList({
        wardId: loginInfo.wardId,
        hospId: loginInfo.hospId,
      });
      if (result?.chats?.length < 1) {
        const chat = {
          chatId: null,
          chatOffDutyCode: 0,
          chatDisplayName: null,
          isObsolete: 0,
          editable: true,
          key: new Date().getTime(),
          checkSensitive: true,
          categoryId: null,
          levelCategoryInfo: { 1: null, 2: null },
          errorMsg: {},
        };
        result.chats.push(chat);
      }
      if (result?.chats) {
        result.chats.sort((a, b) => a.chatDisplayName?.localeCompare(b.chatDisplayName));
        result.chats.map((item, index) => {
          item.key = new Date().getTime() + index;
          item.checkSensitive = false;
          item.errorMsg = {};
          return item;
        });
      }
      checkError();
      onSaveError();
      setInputValues({ ...inputValues, chats: result.chats });
      setShowAll(false);
    } catch (error) {
      console.log('-----Get chatroom list error----->', error);
    }
  };

  const onSaveError = () => {
    serverErrors?.current?.forEach((error) => {
      switch (error.key) {
        case 'duplicateCategory':
          inputValues.chats?.map((chat, index) => {
            chat.errorMsg.server = null;
            Object.keys(error?.categoryInWard).forEach((key) => {
              if (chat.categoryId?.toString() === key) {
                const wardName = error.categoryInWard[key];
                chat.errorMsg.server = i18n.t('DUTYASSIGN_CREATE_CHATROOM_CATEGORY_DUPLICATE', { value: wardName });
              }
            });
            return chat;
          });
          setInputValues({ ...inputValues });
          break;
        default:
          break;
      }
    });
  };

  const saveChatroomList = async () => {
    const list = inputValues.chats.filter((item) => {
      item.chatDisplayName = item.chatDisplayName?.trim();
      item.categoryId = item.levelCategoryInfo[categoryLevel.SUB_CATEGORY_LEVEL];
      return item.chatDisplayName !== null && item.chatDisplayName !== undefined && item.chatDisplayName?.trim() !== '';
    });
    try {
      const { result } = await dutyAssignAPI.saveDaDutyChatList({
        chats: list,
        action: 'PRE',
      });
      if (result.errors.length > 0) {
        serverErrors.current = result.errors;
        onSaveError();
        return;
      }
      const { statusCode } = await dutyAssignAPI.saveDaDutyChatList({
        chats: list,
        action: 'POST',
      });
      if (statusCode === '0') {
        setAlertInfo({ open: true, text: i18n.t('DUTYASSIGN_CREATE_CHATROOM_SUCCESSFUL'), type: 'success' });
        closePop();
      }
    } catch (error) {
      console.log('-----Save chatroom list error----->', error);
    }
  };

  useEffect(() => {
    getDropDownLists();
  }, []);
  useEffect(() => {
    getChatroomList();
  }, []);

  useEffect(() => {
    checkError();
  }, [sensitiveList, inputValues]);

  useEffect(() => {
    if (inputValues) {
      const index = inputValues.chats.findIndex((item) => {
        return (
          item.errorMsg.chatroomName ||
          item.errorMsg.category ||
          item.errorMsg.server ||
          item.errorMsg.chatroomName?.includes(i18n.t('DUTYASSIGN_CREATE_CHATROOM_NAME_CAN_NOT_CONTAIN'))
        );
      });
      setConfirmDisabled(index !== -1);
    } else {
      setConfirmDisabled(false);
    }
  }, [inputValues]);

  useEffect(() => {
    if (chatroomListRef.current) {
      chatroomListRef.current.scrollTop = chatroomListRef.current.scrollHeight;
    }
  }, [isAddChatroom]);

  const checkError = () => {
    if (inputValues?.chats) {
      inputValues?.chats.map((chat, index) => {
        chat.errorMsg.chatroomName = null;
        chat.errorMsg.category = null;
        for (let i = 0; i < inputValues?.chats.length; i++) {
          if (
            index !== i &&
            chat.chatDisplayName === inputValues?.chats[i].chatDisplayName &&
            chat.chatDisplayName !== '' &&
            inputValues?.chats[i].chatDisplayName !== ''
          ) {
            chat.errorMsg.chatroomName = i18n.t('DUTYASSIGN_TEAMDETAILS_DELETE_REPEATED');
          }
          if (
            index !== i &&
            chat.categoryId &&
            chat.isObsolete === 0 &&
            inputValues?.chats[i].isObsolete === 0 &&
            chat.categoryId === inputValues?.chats[i].categoryId
          ) {
            chat.errorMsg.category = i18n.t('DUTYASSIGN_CREATE_CHATROOM_CATEGORY_DUPLICATE', {
              value: loginInfo.wardName,
            });
          }
        }
        // CHEST PAIN TEAM
        const sensitiveIndex = sensitiveList?.findIndex((item) =>
          chat.chatDisplayName?.toLowerCase().includes(item.toLowerCase())
        );
        if (sensitiveIndex !== -1 && chat.editable !== false && chat.checkSensitive === true) {
          chat.errorMsg.chatroomName = `${i18n.t('DUTYASSIGN_CREATE_CHATROOM_NAME_CAN_NOT_CONTAIN')}"${
            sensitiveList[sensitiveIndex]
          }"`;
        }

        if (
          chat.levelCategoryInfo[categoryLevel.CATEGORY_LEVEL] &&
          !chat.levelCategoryInfo[categoryLevel.SUB_CATEGORY_LEVEL]
        ) {
          chat.errorMsg.category = i18n.t('DUTYASSIGN_CREATE_CHATROOM_SUBCATEGORY_REQUIRED_MSG');
        }

        if (chat.chatDisplayName === null || chat.chatDisplayName === '' || chat.chatDisplayName === undefined) {
          chat.errorMsg.chatroomName = i18n.t('DUTYASSIGN_CREATE_CHATROOM_CHATROOM_NAME_REQUIRED_MSG');
        }
        return chat;
      });
    }
  };

  const addChatroom = () => {
    const chatroom = {
      chatId: null,
      chatOffDutyCode: null,
      chatDisplayName: '',
      isObsolete: 0,
      editable: true,
      key: new Date().getTime(),
      checkSensitive: true,
      categoryId: null,
      levelCategoryInfo: { 1: null, 2: null },
      errorMsg: {},
    };
    inputValues.chats.push(chatroom);
    checkError();
    setInputValues({ ...inputValues });
    setISAddChatroom(inputValues.chats.length);
  };

  const deleteHandle = (index) => {
    inputValues.chats.splice(index, 1);
    if (inputValues.chats.length === 0) {
      const chat = {
        chatId: null,
        chatOffDutyCode: 0,
        chatDisplayName: '',
        isObsolete: 0,
        editable: true,
        key: new Date().getTime(),
        checkSensitive: true,
        categoryId: null,
        levelCategoryInfo: { 1: null, 2: null },
        errorMsg: {},
      };
      inputValues.chats.push(chat);
    }
    checkError();
    setInputValues({ ...inputValues, chats: inputValues.chats });
  };

  const getSubCategoryListWithChatroom = (chatroom) => {
    if (chatroom?.levelCategoryInfo[categoryLevel.CATEGORY_LEVEL]) {
      let list = subCategoryList.filter(
        (category) => category.extra?.parentValue === chatroom?.levelCategoryInfo[categoryLevel.CATEGORY_LEVEL]
      );
      const emptyData = {
        value: -999,
        label: i18n.t('DUTYASSIGN_ROSTER_SELECT_SUB_CATEGORY'),
        text: i18n.t('DUTYASSIGN_ROSTER_SELECT_SUB_CATEGORY'),
      };
      list.unshift(emptyData);
      return list;
    } else {
      return [];
    }
  };

  const chats = () => {
    return inputValues.chats.map((chatroom, index) => {
      const canShow = showAll ? true : chatroom.isObsolete === 0;
      return canShow ? (
        <Box key={`AutoCompleteSelect_${chatroom.key}`}>
          <Box
            display="flex"
            mb="1.875rem"
            style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 0, marginTop: '0.5rem' }}
          >
            <Box style={{ width: '43%', marginRight: '1.275rem' }}>
              <InputBase
                id="setup_chatroom_title"
                value={chatroom.chatDisplayName}
                className={classes.InputBase}
                placeholder={i18n.t('DUTYASSIGN_CREATE_CHATROOM_NAME')}
                onChange={(event) => {
                  if (event.target.value.length > 50) {
                    event.target.value = event.target.value.slice(0, 50);
                  }
                  inputValues.chats[index].chatDisplayName = event.target.value;
                  inputValues.chats[index].checkSensitive = true;
                  checkError();
                  setInputValues({ ...inputValues });
                }}
                disabled={!chatroom.editable}
              />
            </Box>
            <Box style={{ width: '19%', marginRight: '1.275rem' }}>
              <AutoCompleteSelect
                value={chatroom.levelCategoryInfo[categoryLevel.CATEGORY_LEVEL]}
                className={classes.select}
                placeholder={i18n.t('DUTYASSIGN_CREATE_CHATROOM_CATEGORY_TITLE')}
                itemList={categoryList}
                onChange={(value) => {
                  if (value === -999) {
                    inputValues.chats[index].levelCategoryInfo[categoryLevel.CATEGORY_LEVEL] = null;
                    inputValues.chats[index].levelCategoryInfo[categoryLevel.SUB_CATEGORY_LEVEL] = null;
                    inputValues.chats[index].categoryId = null;
                  } else {
                    inputValues.chats[index].levelCategoryInfo[categoryLevel.CATEGORY_LEVEL] = value;
                    if (getSubCategoryListWithChatroom(chatroom)?.length === 2) {
                      inputValues.chats[index].levelCategoryInfo[categoryLevel.SUB_CATEGORY_LEVEL] =
                        getSubCategoryListWithChatroom(chatroom)[1].value;
                    } else {
                      inputValues.chats[index].levelCategoryInfo[categoryLevel.SUB_CATEGORY_LEVEL] = null;
                    }
                    inputValues.chats[index].categoryId =
                      inputValues.chats[index].levelCategoryInfo[categoryLevel.SUB_CATEGORY_LEVEL];
                  }
                  checkError();
                  onSaveError();
                  setInputValues({ ...inputValues });
                }}
                id={`select_category_ ${index}`}
              />
            </Box>
            <Box style={{ width: '19%', marginRight: '1.275rem' }}>
              <AutoCompleteSelect
                value={chatroom.levelCategoryInfo[categoryLevel.SUB_CATEGORY_LEVEL]}
                className={classes.select}
                placeholder={i18n.t('DUTYASSIGN_CREATE_CHATROOM_SUBCATEGORY_TITLE')}
                itemList={getSubCategoryListWithChatroom(chatroom)}
                onChange={(value) => {
                  if (value === -999) {
                    inputValues.chats[index].levelCategoryInfo[categoryLevel.SUB_CATEGORY_LEVEL] = null;
                    inputValues.chats[index].categoryId = null;
                  } else {
                    inputValues.chats[index].levelCategoryInfo[categoryLevel.SUB_CATEGORY_LEVEL] = value;
                    inputValues.chats[index].categoryId =
                      inputValues.chats[index].levelCategoryInfo[categoryLevel.SUB_CATEGORY_LEVEL];
                  }

                  checkError();
                  onSaveError();
                  setInputValues({ ...inputValues });
                }}
                id={`select_subcategory_ ${index}`}
              />
            </Box>

            <Box style={{ width: '12%' }}>
              <AutoCompleteSelect
                value={chatroom.isObsolete}
                className={classes.select}
                placeholder={i18n.t('DUTYASSIGN_CREATE_CHATROOM_ABSOLETE')}
                itemList={isObsoleteList}
                onChange={(value) => {
                  inputValues.chats[index].isObsolete = value;
                  checkError();
                  setInputValues({ ...inputValues });
                }}
                id={`select_isObsolete_ ${index}`}
              />
            </Box>
            <Box style={{ width: '5%', height: '100%', alignItems: 'center' }}>
              <DeleteIcon
                id={`delete_chatroom_${index}`}
                className={chatroom.editable ? classes.deleteIcon : classes.deleteIconDisable}
                onClick={() => {
                  if (chatroom.editable) {
                    deleteHandle(index);
                  }
                }}
                disabled={chatroom.editable}
              />
            </Box>
          </Box>
          <Box style={{ display: 'flex', flexDirection: 'row' }}>
            <Typography className={classes.errorMsg} style={{ width: '40.5%', whiteSpace: 'pre-wrap' }}>
              {inputValues.chats[index].errorMsg.chatroomName ? inputValues.chats[index].errorMsg.chatroomName : ''}
            </Typography>
            {inputValues.chats &&
            (inputValues.chats[index].errorMsg?.category || inputValues.chats[index].errorMsg?.server) ? (
              <Typography className={classes.errorMsg} style={{ width: '51%', whiteSpace: 'pre-wrap' }}>
                {inputValues.chats[index].errorMsg.server || inputValues.chats[index].errorMsg.category}
              </Typography>
            ) : (
              ''
            )}
          </Box>
        </Box>
      ) : null;
    });
  };

  const getLineHeight = () => {
    let height = 19;
    const showList = showAll ? inputValues.chats : inputValues.chats.filter((item) => item.isObsolete === 0);
    showList.forEach((item) => {
      if (item.errorMsg?.chatroomName || item.errorMsg?.category || item.errorMsg?.server) {
        height += 6.5;
      } else {
        height += 4.1;
      }
    });
    return `${height}rem`;
  };

  const dialogHeight = getLineHeight();
  return inputValues.chats?.length > 0 ? (
    <Dialog
      width="80rem"
      dialogRootStyle={{ overflow: 'hidden', height: dialogHeight }}
      dialogContentStyle={{ height: '100%', overflow: 'hidden' }}
      dialogfootStyle={{ marginTop: '1.5rem' }}
      open={open}
      disabled={confirmDisabled}
      className={classes.dialog}
      title={title}
      cancel={i18n.t('DUTYASSIGN_CANCEL')}
      confirm={i18n.t('DUTYASSIGN_CREATE_CHATROOM_SAVE')}
      cancelID="dutyAssign-create-chatroom-dialog-ahd-cancel"
      confirmID="dutyAssign-create-chatroom-dialog-ahd-confirm"
      closeID="dutyAssign-create-chatroom-dialog-ahd-close"
      handleClose={() => {
        closePop();
      }}
      handleCancel={() => {
        closePop();
      }}
      handleConfirm={() => {
        saveChatroomList();
      }}
    >
      <Box
        style={{ height: '100%', overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}
      >
        <Box
          display="flex"
          alignItems="center"
          gridGap="0.75rem"
          style={{ flexDirection: 'row', justifyContent: 'flex-start', marginLeft: '82%' }}
        >
          <FormControlLabel
            id="setup_chatroom_show_all"
            control={<Checkbox />}
            label={i18n.t('DUTYASSIGN_CREATE_CHATROOM_SHOW_ALL')}
            className={classes.checkBox}
            checked={showAll}
            onChange={(event) => {
              setShowAll(event.target.checked);
            }}
          />
        </Box>
        {inputValues.chats?.length > 0 ? (
          <Box style={{ marginBottom: '0.75rem', height: '100%', overflow: 'hidden', position: 'relative' }}>
            <Box display="flex" style={{ flexDirection: 'row', width: '100%', marginBottom: '0.2rem' }}>
              <Box style={{ width: '41.5%', marginRight: '1.275rem' }}>
                <Typography className={classes.label}>{i18n.t('DUTYASSIGN_CREATE_CHATROOM_NAME')}</Typography>
              </Box>
              <Box style={{ width: '18%', marginRight: '1.275rem' }}>
                <Typography className={classes.label}>{i18n.t('DUTYASSIGN_CREATE_CHATROOM_CATEGORY_TITLE')}</Typography>
              </Box>
              <Box style={{ width: '18%', marginRight: '1.275rem' }}>
                <Typography className={classes.label}>
                  {i18n.t('DUTYASSIGN_CREATE_CHATROOM_SUBCATEGORY_TITLE')}
                </Typography>
              </Box>

              <Box style={{ width: '11%' }}>
                <Typography className={classes.label}>{i18n.t('DUTYASSIGN_CREATE_CHATROOM_ABSOLETE')}</Typography>
              </Box>
              <Box style={{ width: '5%' }}>
                <Typography className={classes.label}> </Typography>
              </Box>
            </Box>
            <Box ref={chatroomListRef} style={{ height: '95%', overflow: 'auto', paddingBottom: '1.5rem' }}>
              {chats()}
            </Box>
          </Box>
        ) : null}
        <Box display="flex" alignItems="center">
          <ButtonBase
            id="setup_chatroom_add_chatroom"
            className={classes.addBtn}
            style={{ marginRight: '1.313rem' }}
            onClick={addChatroom}
          >
            <AddIcon className={classes.addIcon} />
            {i18n.t('DUTYASSIGN_TEAMDETAILS_ADD_CHATROOM')}
          </ButtonBase>
        </Box>
      </Box>
    </Dialog>
  ) : null;
}
